import React, { useEffect, useRef, useState } from 'react';

import { QBox, QButton, QStack, QTextField, QTypography } from '@captivateiq/quotient';

const MAX_LENGTH = 1000;
const CHAR_COUNT_THRESHOLD = 800;

type FeedbackTextFormProps = {
  messageId: string;
  existingResponse?: string | null;
  onSubmit: (messageId: string, text: string) => Promise<void>;
};

export const FeedbackTextForm: React.FC<FeedbackTextFormProps> = ({ messageId, existingResponse, onSubmit }) => {
  const [text, setText] = useState(existingResponse ?? '');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async () => {
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(messageId, text.trim());
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <QBox sx={{ mt: 0.5 }}>
      <QTextField
        data-testid="feedback-text-input"
        inputRef={inputRef}
        maxRows={3}
        multiline
        optional
        placeholder="Tell us more (optional)"
        slotProps={{ htmlInput: { maxLength: MAX_LENGTH } }}
        sx={{ width: '100%' }}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      <QStack alignItems="center" direction="row" justifyContent="flex-end" spacing="8px" sx={{ mt: 0.5 }}>
        {text.length > CHAR_COUNT_THRESHOLD && (
          <QTypography color="text.secondary" variant="smallRegular">
            {MAX_LENGTH - text.length}
          </QTypography>
        )}
        <QButton
          data-testid="feedback-text-submit"
          disabled={!text.trim() || submitting}
          size="small"
          variant="text"
          onClick={handleSubmit}
        >
          Submit
        </QButton>
      </QStack>
    </QBox>
  );
};
